<template>
  <b-modal
    id="modal-agregar-adicional-gestor"
    :visible="value"
    title="Agregar adicional"
    size="md"
    centered
    @change="$emit('input', $event)"
    @hidden="handleHidden"
    @shown="handleShown"
  >
    <b-alert v-if="targetOs" show variant="light" class="mb-3 small gc-agregar-manual-context">
      OS: <strong>{{ targetOs.detSequenceId || targetOs.nroOs }}</strong> ·
      Tienda: <strong>{{ targetOs.nombreTienda || targetOs.codTienda }}</strong>
    </b-alert>

    <b-form-group label="Servicio adicional (*)" label-size="sm">
      <b-input-group size="sm">
        <b-form-select
          v-model="form.skuServicio"
          :options="serviciosOptions"
          :disabled="loadingServicio || loadingCatalogo"
          @change="onServicioChange"
        >
          <template #first>
            <b-form-select-option :value="''" disabled>
              -- Selecciona un servicio adicional --
            </b-form-select-option>
          </template>
        </b-form-select>
        <b-input-group-append v-if="loadingServicio || loadingCatalogo">
          <b-input-group-text><b-spinner small /></b-input-group-text>
        </b-input-group-append>
      </b-input-group>
      <small v-if="!loadingCatalogo && !serviciosOptions.length" class="text-danger">
        No se encontraron servicios adicionales disponibles.
      </small>
    </b-form-group>

    <b-row>
      <b-col cols="6">
        <b-form-group label="SKU producto (*)" label-size="sm">
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="form.skuProducto"
              placeholder="Ingresa SKU"
              size="sm"
              class="flex-grow-1 mr-2"
              @input="form.descripcionProducto = ''"
              @keyup.enter="buscarProducto"
            />
            <b-button
              class="btn-buscar-sku"
              size="sm"
              :disabled="!form.skuProducto || loadingProducto"
              @click="buscarProducto"
            >
              <b-spinner v-if="loadingProducto" small class="mr-1" />
              <font-awesome-icon v-else icon="search" class="mr-1" />
              <span>Buscar</span>
            </b-button>
          </div>
        </b-form-group>
      </b-col>
      <b-col cols="6">
        <b-form-group label="Descripción producto" label-size="sm">
          <b-form-input size="sm" :value="form.descripcionProducto" disabled />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="4">
        <b-form-group label="Cantidad (*)" label-size="sm">
          <b-form-input
            v-model.number="form.cantidad"
            type="number"
            min="1"
            size="sm"
          />
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group label="C. Sin IGV" label-size="sm">
          <b-form-input
            size="sm"
            :value="form.costoSinIgv !== null && form.costoSinIgv !== undefined ? `S/ ${form.costoSinIgv}` : '—'"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col cols="4">
        <b-form-group label="Total Sin IGV" label-size="sm">
          <b-form-input size="sm" :value="`S/ ${totalLinea}`" disabled />
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group label="Observación" label-size="sm">
      <b-form-textarea
        v-model="form.obsProveedor"
        rows="2"
        maxlength="500"
        placeholder="Detalle o motivo del adicional"
        size="sm"
      />
    </b-form-group>

    <b-alert v-if="error" show variant="warning" class="py-1 small mb-0">
      {{ error }}
    </b-alert>

    <template #modal-footer>
      <b-button
        size="sm"
        variant="secondary"
        class="gc-modal-btn"
        @click="$emit('input', false)"
      >
        Cancelar
      </b-button>
      <b-button
        size="sm"
        class="bg-orange-btn gc-modal-btn"
        :disabled="loadingServicio || loadingProducto || loadingCatalogo"
        @click="handleGuardar"
      >
        Agregar a la lista
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import {
  fetchServicioAdicionalCostos,
  fetchProductBySku,
  fetchAdditionalServicesCatalog,
} from "@/services/liquidaciones-proveedor/liquidaciones-proveedor.api";
import { mapAdditionalServicesSelectOptions } from "@/services/liquidaciones-proveedor/liquidaciones-proveedor.mapper";

function createEmptyForm() {
  return {
    skuServicio: "",
    descripcionServicio: "",
    skuProducto: "",
    descripcionProducto: "",
    cantidad: 1,
    costoSinIgv: null,
    costoConIgv: null,
    obsProveedor: "",
  };
}

export default {
  name: "GestorCentralizadoModalAgregarAdicional",

  props: {
    value: { type: Boolean, default: false },
    targetOs: { type: Object, default: null },
  },

  data() {
    return {
      form: createEmptyForm(),
      serviciosOptions: [],
      loadingCatalogo: false,
      loadingServicio: false,
      loadingProducto: false,
      error: "",
    };
  },

  computed: {
    ...mapState("module", ["token"]),
    totalLinea() {
      return (
        Number(this.form.costoSinIgv || 0) * Number(this.form.cantidad || 0)
      ).toFixed(2);
    },
  },

  methods: {
    async handleShown() {
      this.form = createEmptyForm();
      this.error = "";
      if (!this.serviciosOptions.length) {
        await this.cargarCatalogo();
      }
    },

    handleHidden() {
      this.form = createEmptyForm();
      this.error = "";
      this.$emit("input", false);
    },

    async cargarCatalogo() {
      this.loadingCatalogo = true;
      try {
        const data = await fetchAdditionalServicesCatalog({ token: this.token });
        this.serviciosOptions = mapAdditionalServicesSelectOptions(data);
      } catch (e) {
        this.serviciosOptions = [];
      } finally {
        this.loadingCatalogo = false;
      }
    },

    async onServicioChange(sku) {
      if (!sku || !this.targetOs) return;
      this.loadingServicio = true;
      this.error = "";
      try {
        const storeCode = this.targetOs.codTienda ?? this.targetOs.codigoTienda;
        const data = await fetchServicioAdicionalCostos({
          skuServicio: sku,
          tiendaCodigo: storeCode,
        });
        this.form.descripcionServicio = data.descripcionServicio || "";
        this.form.costoSinIgv = data.costoSinIgv;
        this.form.costoConIgv = data.costoConIgv;
      } catch (err) {
        this.form.descripcionServicio = "";
        this.form.costoSinIgv = null;
        this.form.costoConIgv = null;
        this.error = "No se encontró tarifa vigente para este servicio en la tienda.";
      } finally {
        this.loadingServicio = false;
      }
    },

    async buscarProducto() {
      if (!this.form.skuProducto) return;
      this.loadingProducto = true;
      this.error = "";
      try {
        const data = await fetchProductBySku(this.form.skuProducto, {
          token: this.token,
        });
        if (data?.description) {
          this.form.descripcionProducto = data.description;
        } else {
          this.form.descripcionProducto = "";
          this.error = "No se encontró descripción para el SKU de producto ingresado.";
        }
      } catch (err) {
        this.form.descripcionProducto = "";
        this.error = "Error al validar el SKU del producto.";
      } finally {
        this.loadingProducto = false;
      }
    },

    handleGuardar() {
      if (!this.form.skuServicio) {
        this.error = "Selecciona un SKU de servicio adicional.";
        return;
      }
      if (this.form.costoSinIgv === null || this.form.costoSinIgv === undefined) {
        this.error = "El servicio no tiene tarifa vigente para esta tienda.";
        return;
      }
      if (!this.form.skuProducto) {
        this.error = "Ingresa el SKU del producto.";
        return;
      }
      if (!this.form.descripcionProducto) {
        this.error = "Busca el producto para validar que el SKU exista.";
        return;
      }
      if (!Number(this.form.cantidad) || Number(this.form.cantidad) < 1) {
        this.error = "La cantidad debe ser mayor a 0.";
        return;
      }

      this.error = "";
      this.$emit("agregar", { ...this.form });
      this.$emit("input", false);
    },
  },
};
</script>
